import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../master/SideBar";

import * as XLSX from "xlsx";
import Nav from "../../master/NavBar";
import MarketingHeader from "./MarketingHeader";
import { GetMarketingSchool, MarketingStudentImport } from "../../../api/MarketinService";
import { getSessions } from "../../../api/SessionsService";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";

const FIELDS = [
    { key: "name", label: "Name", aliases: ["name", "student name", "full name", "candidate name"] },
    { key: "fathers_name", label: "Father Name", aliases: ["father name", "father's name", "fathers name", "father", "guardian name"] },
    { key: "mobile", label: "Mobile", aliases: ["mobile", "mobile no", "mobile number", "phone", "phone number", "contact"] },
    { key: "alternate_no", label: "Alternate No", aliases: ["alternate no", "alternate number", "alternate mobile", "other mobile"] },
    { key: "state", label: "State", aliases: ["state"] },
    { key: "district", label: "District", aliases: ["district"] },
    { key: "block", label: "Block", aliases: ["block"] },
    { key: "village", label: "Village", aliases: ["village", "city", "town"] },
    { key: "tehsil", label: "Tehsil", aliases: ["tehsil", "taluka"] },
    { key: "address", label: "Address", aliases: ["address", "full address", "location"] },
    { key: "pincode", label: "Pincode", aliases: ["pincode", "pin code", "postal code", "zip"] },
    { key: "edu_level", label: "Education Level", aliases: ["education level", "education", "qualification", "edu level"] },
    { key: "description", label: "Description", aliases: ["description", "remarks", "comment", "notes"] },
];
const norm = (value) => String(value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

export default function MarketingStudentImportPage() {
    const [sources, setSources] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [selectedSource, setSelectedSource] = useState("");
    const [selectedSession, setSelectedSession] = useState("");
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [rows, setRows] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [reading, setReading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [paused, setPaused] = useState(false);
    const [progress, setProgress] = useState(0);
    const [processedRows, setProcessedRows] = useState(0);
    const [totalImportRows, setTotalImportRows] = useState(0);
    const [currentImportRow, setCurrentImportRow] = useState(null);
    const [requestDelaySeconds, setRequestDelaySeconds] = useState(2);
    // Refs keep controls responsive while the async import loop is running.
    const importingRef = useRef(false);
    const pausedRef = useRef(false);
    const stopRequestedRef = useRef(false);
    const requestDelayRef = useRef(2);
    const [currentFile, setCurrentFile] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        (async () => {
            setLoadingOptions(true);
            try {
                const [sourceRes, sessionRes] = await Promise.all([GetMarketingSchool(), getSessions()]);
                const sourceData = sourceRes?.data?.data ?? sourceRes?.data ?? [];
                const sessionData = sessionRes?.data?.data ?? sessionRes?.data ?? [];
                setSources(Array.isArray(sourceData) ? sourceData : []);
                setSessions(Array.isArray(sessionData) ? sessionData : []);
                const active = (Array.isArray(sessionData) ? sessionData : []).find((item) => String(item?.status) === "1");
                if (active) setSelectedSession(String(active.id));
            } catch (error) {
                SweetAlertShow("error", "Error", error?.response?.data?.message || error?.message || "Source/session list load nahi hui.");
            } finally { setLoadingOptions(false); }
        })();
    }, []);

    const mappedRows = useMemo(() => rows.slice((page - 1) * pageSize, page * pageSize), [rows, page]);
    const successCount = rows.filter((row) => row.status === "success").length;
    const failedCount = rows.filter((row) => row.status === "error").length;
    const pendingCount = rows.filter((row) => row.status === "pending").length;
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

    const autoMapping = (cols) => {
        const result = {};
        FIELDS.forEach((field) => {
            const aliases = [field.key, field.label, ...field.aliases].map(norm);
            const found = cols.find((col) => aliases.includes(norm(col))) || cols.find((col) => aliases.some((alias) => alias && norm(col).includes(alias)));
            result[field.key] = found || "";
        });
        return result;
    };

    const readExcel = async (file) => {
        if (!file) return;
        setReading(true);
        if (importingRef.current) return;
        setRows([]); setHeaders([]); setMapping({}); setPage(1); setProgress(0);
        setProcessedRows(0); setTotalImportRows(0); setCurrentImportRow(null); setCurrentFile(file.name);
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
            if (!raw.length || !raw[0]?.some((cell) => String(cell).trim())) {
                SweetAlertShow("warning", "Empty file", "Excel ki first row mein column headings honi chahiye.");
                return;
            }
            const cols = raw[0].map((value, index) => String(value || `Column ${index + 1}`).trim());
            const auto = autoMapping(cols);
            setHeaders(cols); setMapping(auto);
            const parsed = raw.slice(1).filter((line) => line.some((cell) => String(cell ?? "").trim())).map((line) => {
                const data = {};
                FIELDS.forEach((field) => {
                    const col = auto[field.key];
                    const index = col ? cols.indexOf(col) : -1;
                    data[field.key] = index >= 0 ? String(line[index] ?? "").trim() : "";
                });
                return { data, __raw: line, status: "pending", message: "" };
            });
            setRows(parsed);
            if (!parsed.length) SweetAlertShow("warning", "No data", "Excel file mein import karne ke liye rows nahi mili.");
        } catch (error) {
            SweetAlertShow("error", "Excel error", error?.message || "Excel file read nahi ho paayi.");
        } finally { setReading(false); }
    };

    const applyMapping = () => {
        setRows((old) => old.map((row) => {
            const data = {};
            FIELDS.forEach((field) => {
                const col = mapping[field.key];
                const idx = col ? headers.indexOf(col) : -1;
                data[field.key] = idx >= 0 ? String(row.__raw?.[idx] ?? "").trim() : "";
            });
            return { ...row, data, status: "pending", message: "" };
        }));
    };

    const updateMapping = (key, value) => {
        setMapping((old) => ({ ...old, [key]: value }));
    };

    const updateCell = (rowIndex, key, value) => {
        setRows((old) => old.map((row, index) => index === rowIndex ? { ...row, data: { ...row.data, [key]: value }, status: "pending", message: "" } : row));
    };

    const validateRow = (row) => {
        const data = row.data;
        if (!selectedSession) return "Session select karna zaroori hai.";
        if (!String(data.mobile || "").trim()) return "Mobile required hai.";
        const mobile = String(data.mobile).replace(/[\s()+-]/g, "");
        if (!/^\d{10,15}$/.test(mobile)) return "Mobile number 10–15 digits ka hona chahiye.";
        if (!selectedSource && !String(data.name || "").trim()) return "Name required hai jab source select nahi hai.";
        if (!selectedSource && !String(data.fathers_name || "").trim()) return "Father Name required hai jab source select nahi hai.";
        if (data.alternate_no) {
            const alt = String(data.alternate_no).replace(/[\s()+-]/g, "");
            if (!/^\d{10,15}$/.test(alt)) return "Alternate number 10–15 digits ka hona chahiye.";
        }
        return "";
    };

    const sendRows = async (indices) => {
        // Lock synchronously so two batches cannot run at the same time.
        if (importingRef.current) return;
        if (!selectedSession) {
            SweetAlertShow("warning", "Select session", "Import se pehle session select karein.");
            return;
        }

        const toSend = indices.filter((index) => rows[index] && rows[index].status !== "success");
        if (!toSend.length) {
            SweetAlertShow("info", "No rows", "Import karne ke liye pending/failed rows nahi hain.");
            return;
        }

        importingRef.current = true;
        pausedRef.current = false;
        stopRequestedRef.current = false;
        setPaused(false);
        setImporting(true);
        setProcessedRows(0);
        setTotalImportRows(toSend.length);
        setCurrentImportRow(null);
        setProgress(0);

        let completed = 0;
        let successInRun = 0;
        let failedInRun = 0;
        let stopped = false;
        const total = toSend.length;

        try {
            // Sequential: next request starts only after current response/error is handled.
            for (const index of toSend) {
                // Pause takes effect between rows; current API request is allowed to finish.
                while (pausedRef.current && !stopRequestedRef.current) {
                    await new Promise((resolve) => setTimeout(resolve, 150));
                }
                if (stopRequestedRef.current) {
                    stopped = true;
                    break;
                }

                const row = rows[index];
                setCurrentImportRow({ number: index + 1, mobile: row?.data?.mobile || "" });

                const validationError = validateRow(row);
                if (validationError) {
                    failedInRun += 1;
                    setRows((old) => old.map((item, i) =>
                        i === index ? { ...item, status: "error", message: validationError } : item
                    ));
                } else {
                    const payload = { ...row.data, session_id: selectedSession, source_id: selectedSource || null };
                    Object.keys(payload).forEach((key) => {
                        if (typeof payload[key] === "string") payload[key] = payload[key].trim();
                    });
                    payload.mobile = String(payload.mobile || "").replace(/[\s()+-]/g, "");
                    if (payload.alternate_no) {
                        payload.alternate_no = String(payload.alternate_no).replace(/[\s()+-]/g, "");
                    }

                    try {
                        const response = await MarketingStudentImport(payload);
                        const body = response?.data;
                        if (body?.status === true || body?.status === 1 || body?.success === true) {
                            successInRun += 1;
                            setRows((old) => old.map((item, i) =>
                                i === index ? { ...item, status: "success", message: body?.message || "Imported successfully" } : item
                            ));
                        } else {
                            failedInRun += 1;
                            const rawMessage = body?.message || "Import failed";
                            const message = Array.isArray(rawMessage) ? rawMessage.join(", ") : String(rawMessage);
                            setRows((old) => old.map((item, i) =>
                                i === index ? { ...item, status: "error", message } : item
                            ));
                        }
                    } catch (error) {
                        const rawMessage = error?.response?.data?.message || error?.message || "Request failed";
                        const message = Array.isArray(rawMessage) ? rawMessage.join(", ") : String(rawMessage);
                        failedInRun += 1;
                        setRows((old) => old.map((item, i) =>
                            i === index ? { ...item, status: "error", message } : item
                        ));
                    }
                }

                completed += 1;
                setProcessedRows(completed);
                setProgress(Math.round((completed / total) * 100));

                // Wait after this API response before hitting the next API.
                // Pause freezes the wait; Stop skips the next request.
                if (completed < total) {
                    let remainingMs = requestDelayRef.current * 1000;
                    while (remainingMs > 0 && !stopRequestedRef.current) {
                        while (pausedRef.current && !stopRequestedRef.current) {
                            await new Promise((resolve) => setTimeout(resolve, 150));
                        }
                        if (stopRequestedRef.current) break;
                        const step = Math.min(100, remainingMs);
                        await new Promise((resolve) => setTimeout(resolve, step));
                        remainingMs -= step;
                    }
                }
            }

            if (stopped || stopRequestedRef.current) {
                SweetAlertShow("info", "Import stopped", `${completed} of ${total} rows process hui. Pending rows ko baad mein dobara import kar sakte hain.`);
            } else {
                SweetAlertShow(
                    failedInRun ? "warning" : "success",
                    "Import finished",
                    `${completed} rows process hui. Is run mein success: ${successInRun}, failed: ${failedInRun}.`
                );
            }
        } finally {
            importingRef.current = false;
            pausedRef.current = false;
            stopRequestedRef.current = false;
            setPaused(false);
            setImporting(false);
            setCurrentImportRow(null);
        }
    };

    const changeRequestDelay = (value) => {
        const seconds = Number(value);
        requestDelayRef.current = seconds;
        setRequestDelaySeconds(seconds);
    };

    const pauseImport = () => {
        if (!importingRef.current) return;
        pausedRef.current = true;
        setPaused(true);
    };

    const resumeImport = () => {
        pausedRef.current = false;
        setPaused(false);
    };

    const stopImport = () => {
        if (!importingRef.current) return;
        stopRequestedRef.current = true;
        pausedRef.current = false;
        setPaused(false);
    };

    const importAll = () => sendRows(rows.map((_, index) => index).filter((index) => rows[index].status !== "success"));
    const retryFailed = () => sendRows(rows.map((row, index) => row.status === "error" ? index : -1).filter((index) => index >= 0));
    const retryOne = (index) => sendRows([index]);

    const downloadFailed = () => {
        const failed = rows.filter((row) => row.status === "error");
        if (!failed.length) { SweetAlertShow("info", "No failed rows", "Koi failed row nahi hai."); return; }
        const data = failed.map((row) => ({ ...row.data, import_error: row.message }));
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Rows");
        XLSX.writeFile(workbook, "marketing_students_failed_rows.xlsx");
    };

    const downloadTemplate = () => {
        const data = [Object.fromEntries(FIELDS.map((field) => [field.label, ""]))];
        const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Marketing Students"); XLSX.writeFile(wb, "marketing_students_template.xlsx");
    };

    return (
        <>
            {(loadingOptions || reading) && <LoadingDiv />}

            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <Header />
                    <div className="layout-page">
                        <Nav />
                        <div className="content-wrapper">
                            <div className="container-fluid flex-grow-1 container-p-y">
                                <div className="row g-3">
                                    {/* Marketing section header */}
                                    <div className="col-12">
                                        <MarketingHeader />
                                    </div>

                                    {/* Import setup and column mapping */}
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                                                    <div>
                                                        <h5 className="mb-1">Marketing Student Import</h5>
                                                        <small className="text-muted">Excel upload karein, columns map karein aur students ko individually import karein.</small>
                                                    </div>
                                                    <button type="button" className="btn btn-info" onClick={downloadTemplate}>
                                                        Download Template
                                                    </button>
                                                </div>

                                                <div className="row g-3 align-items-end">
                                                    <div className="col-md-4">
                                                        <label className="form-label">Session <span className="text-danger">*</span></label>
                                                        <select className="form-select" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} disabled={importing}>
                                                            <option value="">Select Session</option>
                                                            {sessions.map((item) => (
                                                                <option key={item.id} value={item.id}>
                                                                    {item.session_name || item.name || item.title || item.year || `Session ${item.id}`}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label">Source (Optional)</label>
                                                        <select className="form-select" value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)} disabled={importing}>
                                                            <option value="">No Source</option>
                                                            {sources.map((item) => (
                                                                <option key={item.id} value={item.id}>
                                                                    {item.source_name || item.name || item.title || `Source ${item.id}`}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <small className="text-muted">Source select hone par Name aur Father Name optional hain.</small>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label">Upload Excel</label>
                                                        <input type="file" className="form-control" accept=".xlsx,.xls,.csv" disabled={importing || reading} onChange={(e) => readExcel(e.target.files?.[0])} />
                                                    </div>
                                                </div>

                                                {currentFile && (
                                                    <div className="alert alert-light border mt-3 mb-0 py-2">
                                                        <strong>File:</strong> {currentFile}
                                                        <span className="ms-3"><strong>Total Rows:</strong> {rows.length}</span>
                                                    </div>
                                                )}

                                                {headers.length > 0 && (
                                                    <div className="mt-4">
                                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                                            <h6 className="mb-0">Column Mapping</h6>
                                                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={applyMapping} disabled={importing}>Apply Mapping</button>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm align-middle">
                                                                <thead className="table-light">
                                                                    <tr><th>Marketing Field</th><th>Excel Column</th><th>Preview</th></tr>
                                                                </thead>
                                                                <tbody>
                                                                    {FIELDS.map((field) => {
                                                                        const sampleRow = rows[0]?.__raw || [];
                                                                        const selectedColumnIndex = mapping[field.key] ? headers.indexOf(mapping[field.key]) : -1;
                                                                        return (
                                                                            <tr key={field.key}>
                                                                                <td className="fw-semibold">{field.label}</td>
                                                                                <td>
                                                                                    <select className="form-select form-select-sm" value={mapping[field.key] || ""} onChange={(e) => updateMapping(field.key, e.target.value)} disabled={importing}>
                                                                                        <option value="">Select Column</option>
                                                                                        {headers.map((header, i) => {
                                                                                            const selectedByAnotherField = Object.entries(mapping).some(
                                                                                                ([mappedKey, mappedHeader]) => mappedKey !== field.key && mappedHeader === header
                                                                                            );
                                                                                            return (
                                                                                                <option
                                                                                                    key={`${header}-${i}`}
                                                                                                    value={header}
                                                                                                    disabled={selectedByAnotherField}
                                                                                                >
                                                                                                    {header}{selectedByAnotherField ? " (Already selected)" : ""}
                                                                                                </option>
                                                                                            );
                                                                                        })}
                                                                                    </select>
                                                                                </td>
                                                                                <td>
                                                                                    <input className="form-control form-control-sm" readOnly value={selectedColumnIndex >= 0 ? String(sampleRow[selectedColumnIndex] ?? "") : ""} />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-2 mt-3">
                                                            <button type="button" className="btn btn-success" onClick={importAll} disabled={importing || !rows.length}>{importing ? "Importing..." : "Start Import"}</button>
                                                            <button type="button" className="btn btn-warning" onClick={retryFailed} disabled={importing || failedCount === 0}>Retry Failed ({failedCount})</button>
                                                            <button type="button" className="btn btn-danger" onClick={downloadFailed} disabled={failedCount === 0}>Download Failed</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Import summary */}
                                    {rows.length > 0 && (
                                        <div className="col-12">
                                            <div className="row g-3">
                                                <div className="col-md-3 col-6"><div className="card bg-label-primary"><div className="card-body"><small>Total Rows</small><h4 className="mb-0">{rows.length}</h4></div></div></div>
                                                <div className="col-md-3 col-6"><div className="card bg-success text-white"><div className="card-body"><small>Success</small><h4 className="mb-0">{successCount}</h4></div></div></div>
                                                <div className="col-md-3 col-6"><div className="card bg-danger text-white"><div className="card-body"><small>Failed</small><h4 className="mb-0">{failedCount}</h4></div></div></div>
                                                <div className="col-md-3 col-6"><div className="card bg-warning"><div className="card-body"><small>Pending</small><h4 className="mb-0">{pendingCount}</h4></div></div></div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Excel preview */}
                                    {rows.length > 0 && (
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-body">
                                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                                        <div>
                                                            <h5 className="mb-1">Excel Preview</h5>
                                                            <small className="text-muted">Import se pehle data check aur edit karein.</small>
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            <button type="button" className="btn btn-success btn-sm" onClick={importAll} disabled={importing}>{importing ? "Importing..." : "Import Pending"}</button>
                                                            <button type="button" className="btn btn-warning btn-sm" onClick={retryFailed} disabled={importing || !failedCount}>Retry Failed</button>
                                                            <button type="button" className="btn btn-danger btn-sm" onClick={downloadFailed} disabled={!failedCount}>Download Failed</button>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                                        <div>
                                                            <strong>{importing ? (paused ? "Import paused" : "Import running") : "Import progress"}</strong>
                                                            <small className="text-muted ms-2">{processedRows} / {totalImportRows} rows processed</small>
                                                            {currentImportRow && !paused && (
                                                                <div className="small text-muted">
                                                                    Current row: #{currentImportRow.number}
                                                                    {currentImportRow.mobile ? ` · Mobile: ${currentImportRow.mobile}` : ""}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <label htmlFor="requestDelaySeconds" className="form-label mb-0 small">Select</label>
                                                            <select
                                                                id="requestDelaySeconds"
                                                                className="form-select form-select-sm"
                                                                style={{ width: 90 }}
                                                                value={requestDelaySeconds}
                                                                onChange={(e) => changeRequestDelay(e.target.value)}
                                                            >
                                                                <option value={1}>1 sec</option>
                                                                <option value={2}>2 sec</option>
                                                                <option value={3}>3 sec</option>
                                                                <option value={5}>5 sec</option>
                                                            </select>
                                                        </div>
                                                        {importing && (
                                                            <div className="d-flex gap-2">
                                                                {!paused ? (
                                                                    <button type="button" className="btn btn-warning btn-sm" onClick={pauseImport}>
                                                                        <i className="ri-pause-line me-1"></i>Pause
                                                                    </button>
                                                                ) : (
                                                                    <button type="button" className="btn btn-success btn-sm" onClick={resumeImport}>
                                                                        <i className="ri-play-line me-1"></i>Resume
                                                                    </button>
                                                                )}
                                                                <button type="button" className="btn btn-danger btn-sm" onClick={stopImport}>
                                                                    <i className="ri-stop-line me-1"></i>Stop
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="progress mb-3" style={{ height: 22 }}>
                                                        <div className={`progress-bar ${importing && !paused ? "progress-bar-striped progress-bar-animated" : ""}`} role="progressbar" style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">{progress}%</div>
                                                    </div>

                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-hover table-sm align-middle">
                                                            <thead className="table-dark">
                                                                <tr><th>SN</th>{FIELDS.map((field) => <th key={field.key} style={{ minWidth: 150 }}>{field.label}</th>)}<th style={{ minWidth: 190 }}>Status / Action</th></tr>
                                                            </thead>
                                                            <tbody>
                                                                {mappedRows.map((row, localIndex) => {
                                                                    const index = (page - 1) * pageSize + localIndex;
                                                                    return (
                                                                        <tr key={index} className={row.status === "error" ? "table-danger" : row.status === "success" ? "table-success" : ""}>
                                                                            <td>{index + 1}</td>
                                                                            {FIELDS.map((field) => (
                                                                                <td key={field.key} className="p-1">
                                                                                    <input type="text" className="form-control form-control-sm" value={row.data[field.key] ?? ""} disabled={importing || row.status === "success"} onChange={(e) => updateCell(index, field.key, e.target.value)} />
                                                                                </td>
                                                                            ))}
                                                                            <td>
                                                                                {row.status === "success" && <span className="badge bg-success">Imported</span>}
                                                                                {row.status === "pending" && <span className="badge bg-secondary">Pending</span>}
                                                                                {row.status === "error" && <>
                                                                                    <span className="badge bg-danger">Failed</span>
                                                                                    <button type="button" className="btn btn-warning btn-sm ms-2" onClick={() => retryOne(index)} disabled={importing}>Retry</button>
                                                                                    <div className="text-danger mt-1" style={{ fontSize: 11 }}>{row.message}</div>
                                                                                </>}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <small className="text-muted">Page {page} of {totalPages} · Showing {rows.length ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, rows.length)} of {rows.length}</small>
                                                        <div className="btn-group btn-group-sm">
                                                            <button type="button" className="btn btn-outline-secondary" disabled={page <= 1 || importing} onClick={() => setPage((p) => p - 1)}>Previous</button>
                                                            <button type="button" className="btn btn-outline-secondary" disabled={page >= totalPages || importing} onClick={() => setPage((p) => p + 1)}>Next</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!rows.length && !reading && (
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-body text-center text-muted py-5">
                                                    <i className="ri-file-excel-2-line d-block mb-2" style={{ fontSize: 42 }}></i>
                                                    <h5>Upload Marketing Student Excel</h5>
                                                    <p className="mb-0">Excel ki first row mein column headings rakhein. File upload karne ke baad mapping aur preview yahan show hoga.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
